import cookie from 'js-cookie';
import * as Enums from '@/utils/enums';
import helper from '@/utils/helper';

// NOTE: This module is now backed by localStorage, not cookies. None of this state is read by
// the server (the API authenticates from the Authorization/tenantid/deviceid request headers and
// the query-string auth handlers), so keeping it in cookies only bloated the `Cookie` header —
// large values (e.g. subscriptionInfo, the JWT) overflowed the nginx ingress header buffers and
// produced "400 Request Header Or Cookie Too Large". localStorage is per-origin, ~5MB, and is
// never transmitted, which removes that whole class of failure.
//
// The exported method names (setCookie/getCookie/…) are kept so existing call sites don't change.

// Keys whose values are objects and therefore need JSON round-tripping. Everything else is stored
// as a plain string (matching the previous cookie behaviour where callers got raw strings back).
const jsonKeys = [Enums.Cookie.permissions, Enums.Cookie.subscriptionInfo, Enums.Cookie.blockMessage];

// Namespace so we can clear only our keys without touching the separate Enums.LocalStorage items
// (running timers, hubspot token, etc.) that are stored directly under their own bare keys.
const PREFIX = 'sc:';
const k = (key: any) => `${PREFIX}${key}`;

const hasWindow = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const setCookie = (key: any, value: any, _duration = 7) => {
    // _duration is retained for call-site compatibility; localStorage has no expiry. Time-sensitive
    // items (the JWT) rely on their own exp + the existing 401 handling.
    // remove industry list as it is not important after signup
    if (key === Enums.Cookie.subscriptionInfo) {
        if (value?.SetupComplete) {
            value = { ...value, IndustryList: [] };
        }

        // override LockedWithOutAccess to Live so all downstream checks see full access
        if (getCookie('bypassLockout') === 'true'
            && value?.AccessStatus === Enums.AccessStatus.LockedWithOutAccess) {
            value = { ...value, AccessStatus: Enums.AccessStatus.Live };
        }
    }

    if (!hasWindow) return;

    try {
        const setVal = jsonKeys.indexOf(key) > -1 ? JSON.stringify(value) : String(value);
        window.localStorage.setItem(k(key), setVal);
    } catch {
        // storage full / unavailable — fail soft, same as a cookie that couldn't be set
    }
};

const getCookie = (key: any) => {
    if (!hasWindow) return null;

    const raw = window.localStorage.getItem(k(key));
    if (raw === null) return null;

    if (jsonKeys.indexOf(key) > -1) {
        try {
            return JSON.parse(raw);
        } catch {
            window.localStorage.removeItem(k(key));
            return null;
        }
    }
    return raw;
};

/** function that clears all app storage (the former cookie keys) **/
const clearCookie = () => {
    if (!hasWindow) return;
    // Remove only our namespaced keys; leaves Enums.LocalStorage items intact (matches the old
    // behaviour where clearing cookies didn't wipe localStorage).
    const toRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && key.startsWith(PREFIX)) toRemove.push(key);
    }
    toRemove.forEach((key) => window.localStorage.removeItem(key));
};

const hasCookieValue = (key: any) => {
    return !helper.isNullOrWhitespace(getCookie(key));
}

/**
 * One-time migration: copy any pre-existing cookies into localStorage (so logged-in users keep
 * their session) and then delete every cookie so the bloated `Cookie` header stops being sent.
 * Runs once at module load; idempotent (skips keys already present in localStorage).
 */
const migrateLegacyCookies = () => {
    if (!hasWindow) return;

    // Only touch cookies this app owns — don't disturb third-party cookies (analytics, HubSpot, …)
    // that may live on the same origin.
    const ownedKeys = [...Object.values(Enums.Cookie), 'bypassLockout'];

    ownedKeys.forEach((key: any) => {
        let legacy: string | undefined;
        try {
            legacy = cookie.get(key);
        } catch {
            return;
        }
        if (legacy === undefined) return;

        try {
            if (window.localStorage.getItem(k(key)) === null) {
                if (jsonKeys.indexOf(key) > -1) {
                    // legacy object cookies were base64(JSON); decode and re-store as plain JSON
                    try {
                        const obj = JSON.parse(atob(legacy));
                        window.localStorage.setItem(k(key), JSON.stringify(obj));
                    } catch {
                        // corrupt legacy value — drop it
                    }
                } else {
                    window.localStorage.setItem(k(key), legacy);
                }
            }
        } finally {
            cookie.remove(key);
        }
    });
};

migrateLegacyCookies();

export default {
    setCookie,
    getCookie,
    clearCookie,
    hasCookieValue
};
